import { PureComponent } from 'react';
import ReactDOM from 'react-dom';

/**
 * @typedef {Object} WindowProps
 * @property {string} title - Window title
 * @property {number} width - Window width
 * @property {number} height - Window height
 * @property {() => void} onBlock - Function called when popup is blocked
 * @property {() => void} onClose - Function called when popup is closed
 * @property {'parent' | 'screen'} center - How to center the window
 * @property {boolean} copyStyles - Whether to copy parent styles
 * @property {any} children - Child components to render
 */

export default class PopupWindow extends PureComponent {
  /**
   * @param {WindowProps} props
   */
  constructor(props) {
    super(props);
    this.container = document.createElement('div');
    this.window = null;
  }

  componentDidMount() {
    /** @type {any} */
    const features = {
      width: this.props.width,
      height: this.props.height
    };

    // Prepare position of the new window to be centered against the 'parent' window or 'screen'.
    if (this.props.center === 'parent') {
      features.left =
        window.top.outerWidth / 2 + window.top.screenX - this.props.width / 2;
      features.top =
        window.top.outerHeight / 2 + window.top.screenY - this.props.height / 2;
    } else if (this.props.center === 'screen') {
      features.left =
        window.innerWidth / 2 - features.width / 2 + window.screenLeft;
      features.top =
        window.innerHeight / 2 - features.height / 2 + window.screenTop;
    }

    // Open a new window.
    this.window = window.open('about:blank', '', toWindowFeatures(features));

    // Check if the new window was succesfully opened.
    if (this.window != null) {
      // Copy to a const to keep flow from complaining that window needs to be null-checked again
      const window = this.window;

      window.document.title = this.props.title;
      window.document.body.appendChild(this.container);

      // If specified, copy styles from parent window's document.
      if (this.props.copyStyles) {
        setTimeout(() => {
          copyStyles(document, window.document);
        }, 0);
      }

      // Release anything bound to this component before the new window unload.
      window.addEventListener('beforeunload', this.props.onClose);
    } else {
      this.props.onBlock();
    }
  }

  componentWillUnmount() {
    if (this.window != null) {
      this.window.close();
    }
  }

  focus() {
    if (this.window != null) {
      this.window.focus();
    }
  }

  /** @type {HTMLDivElement} */
  container;

  /** @type {PopupWindow | null} */
  window;

  render() {
    return ReactDOM.createPortal(this.props.children, this.container);
  }
}

/**
 * Utility functions.
 * @private
 */

/**
 * Copy styles from a source document to a target.
 * @param {Object} source
 * @param {Object} target
 * @private
 */

function copyStyles(source, target) {
  Array.from(source.styleSheets).forEach(styleSheet => {
    // For <style> elements
    let rules;
    try {
      rules = styleSheet.cssRules;
    } catch (err) {
      console.error(err);
    }
    if (rules) {
      const newStyleEl = source.createElement('style');

      // Write the text of each rule into the body of the style element
      Array.from(styleSheet.cssRules).forEach(cssRule => {
        const { cssText, type } = cssRule;
        let returnText = cssText;
        // Check if the cssRule type is CSSImportRule (3) or CSSFontFaceRule (5) to handle local
        // imports on a about:blank page
        // '/custom.css' turns to 'http://my-site.com/custom.css'
        if ([3, 5].includes(type)) {
          returnText = cssText
            .split('url(')
            .map(line => {
              if (line[1] === '/') {
                return `${line.slice(0, 1)}${
                  window.location.origin
                }${line.slice(1)}`;
              }
              return line;
            })
            .join('url(');
        }
        newStyleEl.appendChild(source.createTextNode(returnText));
      });

      target.head.appendChild(newStyleEl);
    } else if (styleSheet.href) {
      // for <link> elements loading CSS from a URL
      const newLinkEl = source.createElement('link');

      newLinkEl.rel = 'stylesheet';
      newLinkEl.href = styleSheet.href;
      target.head.appendChild(newLinkEl);
    }
  });
}

/**
 * Convert features props to window features format (name=value,other=value).
 * @param {Object} obj
 * @return {String}
 * @private
 */

function toWindowFeatures(obj) {
  return Object.keys(obj)
    .reduce((features, name) => {
      const value = obj[name];
      if (typeof value === 'boolean') {
        features.push(`${name}=${value ? 'yes' : 'no'}`);
      } else {
        features.push(`${name}=${value}`);
      }
      return features;
    }, [])
    .join(',');
}
